import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmationResultModal, AsyncSelect, FormField, Spacer, Text, Flex, Badge, Tooltip } from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AccountType } from '@percent/domain/iam'
import type { Account } from '@percent/domain/iam'
import type { Group } from '@percent/admin-dashboard/api/actions/groups/groups.types'
import type { AddMemberModalProps } from './AddMemberModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

type SelectedMember = {
  id: string
  name: string
  type: 'account' | 'group'
  email?: string
}

const MIN_SEARCH_LENGTH = 3

export function AddMemberModal({ isOpen, onClose, groupId, onSuccess }: AddMemberModalProps) {
  const { t } = useTranslation()
  const { groupService, accountService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SelectOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([])
  const accountEmailsRef = useRef<Map<string, string>>(new Map())

  const [{ isPending }, { mutateAsync: addMembers }] = useMutation(groupService.addMembers)

  const handleSearch = useCallback(
    async (query: string) => {
      setSearchQuery(query)

      if (query.length < MIN_SEARCH_LENGTH) {
        setSearchResults([])

        return
      }

      setIsSearching(true)

      try {
        const [accountsResult, groupsResult] = await Promise.allSettled([
          accountService.getAccounts({ search: query, pageSize: 10, type: AccountType.internal }),
          groupService.getGroups({ search: query, pageSize: 10 })
        ])

        const accounts: Account[] = accountsResult.status === 'fulfilled' ? accountsResult.value.data?.data ?? [] : []
        const groups: Group[] = groupsResult.status === 'fulfilled' ? groupsResult.value.data?.data ?? [] : []
        const selectedIds = new Set(selectedMembers.map(m => m.id))

        accounts.forEach(account => accountEmailsRef.current.set(account.id, account.email))

        const accountOptions = accounts
          .filter(account => !selectedIds.has(account.id))
          .map(account => ({
            value: `account:${account.id}`,
            label: account.fullName || account.email,
            badge: { text: t('modal.addMember.memberType.individual') }
          }))

        const groupOptions = groups
          .filter(group => group.id !== groupId && !selectedIds.has(group.id))
          .map(group => ({
            value: `group:${group.id}`,
            label: group.name,
            badge: { text: t('modal.addMember.memberType.group') }
          }))

        setSearchResults([...accountOptions, ...groupOptions])
      } finally {
        setIsSearching(false)
      }
    },
    [accountService, groupId, groupService, selectedMembers, t]
  )

  const handleSelectMember = useCallback(
    (option: SelectOption | null) => {
      if (!option) return

      const [type, id] = option.value.split(':') as ['account' | 'group', string]
      const alreadySelected = selectedMembers.some(m => m.id === id)

      if (alreadySelected) return

      setSelectedMembers(prev => [
        ...prev,
        {
          id,
          name: option.label,
          type,
          email: type === 'account' ? accountEmailsRef.current.get(id) : undefined
        }
      ])
      setSearchQuery('')
      setSearchResults([])
    },
    [selectedMembers]
  )

  const handleSubmit = async () => {
    try {
      await addMembers({
        groupId,
        memberIds: selectedMembers.map(m => m.id)
      })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  const resetState = () => {
    setModalState('confirm')
    setSearchQuery('')
    setSearchResults([])
    setSelectedMembers([])
  }

  const handleClose = () => {
    if (!isPending) {
      resetState()
      onClose()
    }
  }

  const handleSuccessClose = () => {
    resetState()
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
  }

  if (!isOpen) {
    return null
  }

  const isSubmitDisabled = selectedMembers.length === 0 || isPending

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.addMember.title')}
      primaryButtonText={
        isPending
          ? t('modal.addMember.button.adding')
          : selectedMembers.length > 0
          ? t('modal.addMember.button.add')
          : t('modal.addMember.button.saveChanges')
      }
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleSubmit}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      disabled={isSubmitDisabled}
      loading={isPending}
      primaryBtnTestId="add-member-submit-button"
      secondaryBtnTestId="add-member-cancel-button"
      success={{
        title: t('modal.addMember.success.title'),
        description: t('modal.addMember.success.description'),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.addMember.error.title'),
        description: t('modal.addMember.error.description'),
        button: t('button.close')
      }}
    >
      <form>
        <Text size="small">{t('modal.addMember.description')}</Text>
        <Spacer size={4} axis="vertical" />
        <FormField label={t('modal.addMember.search.label')}>
          <AsyncSelect
            data-testid="member-search-select"
            placeholder={t('modal.addMember.search.placeholder')}
            options={searchResults}
            setQuery={handleSearch}
            query={searchQuery}
            onChange={handleSelectMember}
            loading={isSearching}
            noResultsFoundText={t('modal.addMember.search.noResults')}
            clearQueryOnSelect
            minSearchLength={MIN_SEARCH_LENGTH}
            minSearchLengthMessage={t('modal.addMember.search.minLength')}
          />
        </FormField>
        {selectedMembers.length > 0 && (
          <div data-testid="selected-members" style={{ marginTop: '16px', maxHeight: '240px', overflowY: 'auto' }}>
            {selectedMembers.map(member => (
              <Tooltip key={member.id} content={member.email || member.name} placement="top">
                <div
                  style={{
                    borderBottom: '1px solid #e9e8eb',
                    padding: '8px'
                  }}
                >
                  <Flex direction="row" gap="xs" align="center">
                    <Text size="small" fontWeight="medium">
                      {member.name}
                    </Text>
                    <Badge variant="informative">
                      {member.type === 'group'
                        ? t('modal.addMember.memberType.group')
                        : t('modal.addMember.memberType.individual')}
                    </Badge>
                  </Flex>
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </form>
    </ConfirmationResultModal>
  )
}
